import React from 'react';

import { Interweave } from 'interweave';

import { BasicMessage } from '@liscio/api';
import { MessageAttachmentsChips } from '@liscio/common';
import { styled, Box, Chip, Icon, Stack, Typography } from '@liscio/ui';

import { getParticipants } from './helpers';

export interface PrimaryProps {
  message: BasicMessage;
  shouldDisplayAsUnread: boolean;
  showDashboardMetadata?: boolean;
}

const Date = styled(Typography)(({ theme }) => ({
  flexShrink: 0,
  fontSize: '13px',
  color: theme.palette.common.blueGray[500],
}));

const Content = styled(Box)(({ theme }) => ({
  color: theme.palette.common.blueGray[500],
}));

export const Primary: React.FC<PrimaryProps> = ({
  message,
  shouldDisplayAsUnread,
  showDashboardMetadata = false,
}) => {
  const participants = getParticipants(message);
  const shouldShowUnreadCount =
    showDashboardMetadata && Number(message.unread_count) > 1;
  const shouldShowPinned = showDashboardMetadata && Boolean(message.is_pinned);
  const hasDocuments = Boolean(message.documents?.length);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" textAlign="start">
        <Typography variant={shouldDisplayAsUnread ? 'body2' : 'body1'}>
          {participants}
        </Typography>
        <Date>{message.tym}</Date>
      </Box>
      <Box>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1,
            flexWrap: showDashboardMetadata && hasDocuments ? 'nowrap' : 'wrap',
            minWidth: 0,
          }}
        >
          {shouldShowPinned && (
            <Icon
              aria-label="Pinned thread"
              icon="pushpin"
              fontSize="small"
              sx={(theme) => ({
                color: theme.palette.common.accent[500],
                transform: 'rotate(45deg)',
              })}
            />
          )}
          {shouldShowUnreadCount && (
            <Chip
              size="small"
              label={`${message.unread_count} NEW`}
              aria-label={`${message.unread_count} new messages`}
            />
          )}
          <Content
            sx={
              showDashboardMetadata && hasDocuments
                ? {
                    flex: '0 1 auto',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }
                : undefined
            }
          >
            {message.title}
          </Content>
          {showDashboardMetadata && hasDocuments && (
            <MessageAttachmentsChips
              documents={message.documents || []}
              chipSize="small"
              maxNameWidth={140}
              sx={{ mr: 0, flexShrink: 0 }}
            />
          )}
        </Stack>
      </Box>
      <Content>
        <Interweave
          content={
            message.content_limit && message.content !== null
              ? message.content_limit
                  .replace(/html/gi, 'div')
                  .replace(/!doctype/gi, 'div></div')
              : ''
          }
        />
      </Content>
    </Box>
  );
};

export default Primary;
